import React, { Suspense, lazy, useState, useEffect, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext.js';
import { Menu, X, Globe, ChevronDown, Search } from 'lucide-react';
const GlobalSearch = lazy(() => import('./GlobalSearch.js'));

const Navbar = () => {
  const { t, currentLanguage, changeLanguage } = useLanguage();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const [toolsMenuOpen, setToolsMenuOpen] = useState(false);
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const [toolsSearch, setToolsSearch] = useState('');
  const [openMobileCategories, setOpenMobileCategories] = useState({});
  const isRtl = currentLanguage === 'ar';

  const langMenuRef = useRef(null);
  const toolsMenuRef = useRef(null);
  const moreMenuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (langMenuRef.current && !langMenuRef.current.contains(event.target)) setLangMenuOpen(false);
      if (toolsMenuRef.current && !toolsMenuRef.current.contains(event.target)) setToolsMenuOpen(false);
      if (moreMenuRef.current && !moreMenuRef.current.contains(event.target)) setMoreMenuOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const languages = [
    { code: 'en', name: 'English' },
    { code: 'ar', name: 'العربية' },
    { code: 'fr', name: 'Français' },
    { code: 'es', name: 'Español' },
    { code: 'de', name: 'Deutsch' },
    { code: 'zh', name: '中文' },
    { code: 'pt', name: 'Português' },
  ];

  const mainNavLinks = [
    { path: '/', label: t('home') },
  ];

  const toolsMenuCategories = useMemo(
    () => [
      {
        key: 'image-tools',
        label: t('menuImageTools'),
        items: [
          { path: '/tools', label: t('menuExploreImageTools') },
        ],
      },
      {
        key: 'pdf-tools',
        label: t('menuPdfTools'),
        items: [
          { path: '/tools', label: t('menuExplorePdfTools') },
        ],
      },
      {
        key: 'calculator-tools',
        label: t('menuCalculatorTools'),
        items: [
          { path: '/tools/age-calculator', label: t('toolAgeCalculator') },
          { path: '/tools/unit-converter', label: t('toolUnitConverter') },
          { path: '/tools/random-number', label: t('toolRandomNumber') },
        ],
      },
      {
        key: 'text-tools',
        label: t('menuTextTools'),
        items: [
          { path: '/tools/word-counter', label: t('toolWordCounter') },
          { path: '/tools/text-case-converter', label: t('toolTextCaseConverter') },
          { path: '/tools/text-compare', label: t('toolTextCompare') },
          { path: '/tools/translation-tool', label: t('toolTranslationTool') },
        ],
      },
      {
        key: 'utility-tools',
        label: t('menuUtilityTools'),
        items: [
          { path: '/tools/password-generator', label: t('toolPasswordGenerator') },
          { path: '/tools/wheel-of-fortune', label: t('toolWheelOfFortune') },
          { path: '/tools/json-formatter', label: t('toolJSONFormatter') },
        ],
      },
      {
        key: 'ai-tools',
        label: t('menuAiTools'),
        items: [
          { path: '/ai-tools', label: t('aiToolsDirectory') },
          { path: '/ai-finder', label: t('aiFinderNav') },
          { path: '/ai-chat', label: t('aiChat') },
        ],
      },
      {
        key: 'ai-games',
        label: t('menuAiGames'),
        items: [
          { path: '/games', label: t('gamesHub') },
        ],
      },
    ],
    [t]
  );

  const filteredToolsCategories = useMemo(() => {
    const query = toolsSearch.trim().toLowerCase();
    if (!query) return toolsMenuCategories;

    return toolsMenuCategories
      .map((category) => ({
        ...category,
        items: category.items.filter((item) => item.label.toLowerCase().includes(query)),
      }))
      .filter((category) => category.label.toLowerCase().includes(query) || category.items.length > 0);
  }, [toolsMenuCategories, toolsSearch]);

  const resourceLinks = [
    { path: '/football-news', label: t('footballNews') },
    { path: '/programming', label: t('programming') },
    { path: '/gaming', label: t('gaming') },
    { path: '/articles', label: t('readSeoArticles') },
  ];

  const legalLinks = [
    { path: '/about', label: t('aboutUs') },
    { path: '/contact', label: t('contactUs') },
    { path: '/how-to-use', label: t('howToUse') },
    { path: '/privacy-policy', label: t('privacyPolicy') },
    { path: '/terms-of-service', label: t('termsOfService') },
  ];

  const toggleMobileCategory = (categoryKey) => {
    setOpenMobileCategories((prev) => ({ ...prev, [categoryKey]: !prev[categoryKey] }));
  };

  return (
    <nav className="bg-white shadow-md sticky top-0 z-50" data-testid="main-navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <Link to="/" className="flex items-center space-x-2" data-testid="logo-link">
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white font-bold text-xl px-3 py-1 rounded-lg">
              3ToolsPro
            </div>
          </Link>

          <div className="hidden lg:flex items-center space-x-1">
            {mainNavLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className="px-3 py-2 text-gray-700 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all font-medium text-sm"
              >
                {link.label}
              </Link>
            ))}

            <div className="relative" ref={toolsMenuRef}>
              <button
                onClick={() => setToolsMenuOpen(!toolsMenuOpen)}
                className="flex items-center space-x-1 px-3 py-2 text-gray-700 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all font-medium text-sm"
                data-testid="tools-menu-btn"
                aria-expanded={toolsMenuOpen}
              >
                <span>{t('tools')}</span>
                <ChevronDown size={16} className={`transform transition-transform duration-200 ${toolsMenuOpen ? 'rotate-180' : ''}`} />
              </button>

              <div
                className={`absolute left-0 mt-2 w-[42rem] max-w-[calc(100vw-4rem)] bg-white rounded-xl shadow-xl border border-gray-200 p-4 z-50 transition-all duration-200 origin-top ${
                  toolsMenuOpen ? 'opacity-100 scale-100 visible' : 'opacity-0 scale-95 invisible'
                }`}
              >
                <div className="relative mb-4">
                  <Search size={16} className={`absolute top-1/2 -translate-y-1/2 text-gray-400 ${isRtl ? 'right-3' : 'left-3'}`} />
                  <input
                    value={toolsSearch}
                    onChange={(e) => setToolsSearch(e.target.value)}
                    placeholder={t('menuSearchTools')}
                    className={`w-full py-2 text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 ${isRtl ? 'pr-10 pl-3 text-right' : 'pl-10 pr-3 text-left'}`}
                  />
                </div>

                <div className="grid grid-cols-2 gap-3 max-h-[60vh] overflow-y-auto pr-1">
                  {filteredToolsCategories.map((category) => (
                    <div key={category.key} className="rounded-lg border border-gray-100 bg-gray-50/50 p-3">
                      <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">{category.label}</h4>
                      <div className="space-y-1">
                        {category.items.map((item) => (
                          <Link
                            key={`${category.key}-${item.path}-${item.label}`}
                            to={item.path}
                            onClick={() => {
                              setToolsMenuOpen(false);
                              setToolsSearch('');
                            }}
                            className="block rounded-md px-2 py-1.5 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-700 transition-colors"
                          >
                            {item.label}
                          </Link>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                {filteredToolsCategories.length === 0 && (
                  <p className="text-sm text-gray-500 px-1 py-3">{t('searchNoResults')}</p>
                )}
              </div>
            </div>


            <Link
              to="/ai-tools"
              className="px-3 py-2 text-gray-700 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all font-medium text-sm"
            >
              {t('aiToolsDirectory')}
            </Link>

            <Link
              to="/ai-finder"
              className="px-3 py-2 text-gray-700 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all font-medium text-sm"
            >
              {t('aiFinderNav')}
            </Link>

            <div className="relative" ref={moreMenuRef}>
              <button
                onClick={() => setMoreMenuOpen(!moreMenuOpen)}
                className="flex items-center space-x-1 px-3 py-2 text-gray-700 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all font-medium text-sm"
              >
                <span>{t('more')}</span>
                <ChevronDown size={16} className={`transform transition-transform ${moreMenuOpen ? 'rotate-180' : ''}`} />
              </button>

              {moreMenuOpen && (
                <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-50">
                  <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">{t('resources')}</div>
                  {resourceLinks.map((link) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      onClick={() => setMoreMenuOpen(false)}
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors"
                    >
                      {link.label}
                    </Link>
                  ))}

                  <div className="border-t border-gray-200 my-2"></div>

                  <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">{t('legalInfo')}</div>
                  {legalLinks.map((link) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      onClick={() => setMoreMenuOpen(false)}
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors"
                    >
                      {link.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="hidden md:block lg:w-80 xl:w-96">
            <Suspense fallback={<div className="h-10 rounded-xl border border-slate-200 bg-slate-100" aria-hidden="true" />}>
              <GlobalSearch />
            </Suspense>
          </div>

          <div className="flex items-center space-x-2 sm:space-x-4">
            <div className="relative" ref={langMenuRef}>
              <button
                onClick={() => setLangMenuOpen(!langMenuOpen)}
                className="flex items-center space-x-2 px-3 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-all"
                data-testid="language-selector-btn"
              >
                <Globe size={20} />
                <span className="hidden sm:inline text-sm font-medium">{languages.find((l) => l.code === currentLanguage)?.name}</span>
              </button>

              {langMenuOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-50">
                  <div className="px-4 py-2 text-sm font-semibold text-gray-700 border-b">{t('selectLanguage')}</div>
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => {
                        changeLanguage(lang.code);
                        setLangMenuOpen(false);
                      }}
                      className={`w-full px-4 py-2 text-sm hover:bg-blue-50 transition-colors ${isRtl ? 'text-right' : 'text-left'} ${
                        currentLanguage === lang.code ? 'bg-blue-100 text-blue-600' : 'text-gray-700'
                      }`}
                    >
                      <span>{lang.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        <div
          className={`lg:hidden border-t border-gray-200 overflow-hidden transition-all duration-300 ${
            mobileMenuOpen ? 'max-h-[85vh] py-4 overflow-y-auto' : 'max-h-0'
          }`}
          data-testid="mobile-menu"
        >
          <div className="mb-4 px-4">
            <Suspense fallback={<div className="h-10 rounded-xl border border-slate-200 bg-slate-100" aria-hidden="true" />}>
              <GlobalSearch onSubmit={() => setMobileMenuOpen(false)} />
            </Suspense>
          </div>

          <div className="mb-4 px-4 space-y-2">
            {mainNavLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setMobileMenuOpen(false)}
                className="block px-3 py-2 text-gray-700 hover:bg-blue-50 hover:text-blue-600 rounded-lg transition-all"
              >
                {link.label}
              </Link>
            ))}
          </div>

          <div className="border-t border-gray-200 pt-4 mb-4">
            <div className="px-4 mb-3">
              <div className="relative">
                <Search size={16} className={`absolute top-1/2 -translate-y-1/2 text-gray-400 ${isRtl ? 'right-3' : 'left-3'}`} />
                <input
                  value={toolsSearch}
                  onChange={(e) => setToolsSearch(e.target.value)}
                  placeholder={t('menuSearchTools')}
                  className={`w-full py-2 text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 ${isRtl ? 'pr-10 pl-3 text-right' : 'pl-10 pr-3 text-left'}`}
                />
              </div>
            </div>

            <p className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">{t('menuToolsCategories')}</p>
            <div className="space-y-2 px-2">
              {filteredToolsCategories.map((category) => (
                <div key={`mobile-${category.key}`} className="rounded-lg border border-gray-200 bg-white">
                  <button
                    onClick={() => toggleMobileCategory(category.key)}
                    className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium text-gray-700"
                  >
                    <span>{category.label}</span>
                    <ChevronDown
                      size={16}
                      className={`transition-transform duration-200 ${openMobileCategories[category.key] ? 'rotate-180' : ''}`}
                    />
                  </button>

                  <div
                    className={`overflow-hidden transition-all duration-300 ${
                      openMobileCategories[category.key] ? 'max-h-48 opacity-100' : 'max-h-0 opacity-0'
                    }`}
                  >
                    <div className="px-3 pb-2 space-y-1">
                      {category.items.map((item) => (
                        <Link
                          key={`mobile-${category.key}-${item.path}-${item.label}`}
                          to={item.path}
                          onClick={() => {
                            setMobileMenuOpen(false);
                            setToolsSearch('');
                          }}
                          className="block rounded-md px-2 py-1.5 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600"
                        >
                          {item.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="border-t border-gray-200 pt-4 mb-4">
            <p className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">{t('resources')}</p>
            {resourceLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setMobileMenuOpen(false)}
                className="block px-4 py-2 text-gray-700 hover:bg-blue-50 hover:text-blue-600 rounded-lg transition-all"
              >
                {link.label}
              </Link>
            ))}
          </div>

          <div className="border-t border-gray-200 pt-4">
            <p className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">{t('legalInfo')}</p>
            {legalLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setMobileMenuOpen(false)}
                className="block px-4 py-2 text-gray-700 hover:bg-blue-50 hover:text-blue-600 rounded-lg transition-all"
              >
                {link.label}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default React.memo(Navbar);
